/* AlertsManagerInline — slim per-location push-alert list shown inside
 * AccountChip's bottom sheet when the user has Storm-watch alerts enabled.
 *
 * Lists every saved location synced to the backend, lets the user toggle
 * alerts per city, and exposes a single "Send a test alert" button so they
 * can verify their device actually receives a push.
 */
import { useEffect, useState, useCallback } from "react";
import { Bell, BellSlash, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getSavedLocations,
  setLocationAlerts,
  pushTestNotification,
} from "@/lib/api";

export default function AlertsManagerInline({ theme, currentLocation }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await getSavedLocations();
      setItems(r?.locations || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggleOne = async (loc) => {
    if (busyId) return;
    setBusyId(loc.location_id);
    try {
      await setLocationAlerts(loc.location_id, !loc.alerts_enabled);
      setItems((prev) =>
        prev.map((it) =>
          it.location_id === loc.location_id
            ? { ...it, alerts_enabled: !loc.alerts_enabled }
            : it,
        ),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update alert.");
    } finally {
      setBusyId(null);
    }
  };

  const sendTest = async () => {
    if (testing) return;
    setTesting(true);
    try {
      const r = await pushTestNotification();
      const sent = r?.sent ?? 0;
      if (sent > 0) {
        toast.success(`Test push sent to ${sent} device${sent === 1 ? "" : "s"}.`);
      } else {
        toast.message("Subscribed but no active device endpoints — try toggling alerts off and back on.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send test alert.");
    } finally {
      setTesting(false);
    }
  };

  const dark = theme.id === "night" || theme.id === "storm";
  const rowBg = dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)";
  const rowBorder = theme.surfaceBorder;

  if (loading) return null;

  // No saved cities — nudge the user to pin some.
  const hasItems = items.length > 0;

  return (
    <div className="mt-3" data-testid="alerts-manager">
      <div
        className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1.5 px-1"
        style={{ color: theme.textMuted }}
      >
        Watching {hasItems ? `${items.length} ${items.length === 1 ? "place" : "places"}` : "no pinned places"}
      </div>

      {!hasItems && (
        <div
          className="rounded-2xl px-4 py-3 text-[12px]"
          style={{
            background: rowBg,
            border: `1px dashed ${rowBorder}`,
            color: theme.textSecondary,
          }}
          data-testid="alerts-manager-empty"
        >
          Pin a city with the <strong>star icon</strong> on the location bar.
          We'll watch every pinned place and ping you when severe weather builds.
        </div>
      )}

      {hasItems && (
        <div
          className="rounded-2xl divide-y"
          style={{
            background: rowBg,
            border: `1px solid ${rowBorder}`,
            borderColor: rowBorder,
          }}
        >
          {items.map((loc, idx) => {
            const isCurrent =
              currentLocation &&
              Math.abs(Number(currentLocation.latitude) - loc.latitude) < 0.05 &&
              Math.abs(Number(currentLocation.longitude) - loc.longitude) < 0.05;
            return (
              <button
                key={loc.location_id}
                type="button"
                onClick={() => toggleOne(loc)}
                disabled={busyId === loc.location_id}
                data-testid={`alerts-row-${loc.location_id}`}
                className="w-full flex items-center gap-3 px-3.5 py-2.5 transition-colors disabled:opacity-60"
                style={{
                  borderTop: idx === 0 ? "none" : `1px solid ${rowBorder}`,
                }}
              >
                <span
                  className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{
                    background: loc.alerts_enabled
                      ? "rgba(16,185,129,0.18)"
                      : dark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(15,23,42,0.06)",
                    color: loc.alerts_enabled ? "#047857" : theme.textMuted,
                  }}
                >
                  {loc.alerts_enabled ? (
                    <Bell size={13} weight="fill" />
                  ) : (
                    <BellSlash size={13} weight="bold" />
                  )}
                </span>
                <div className="flex-1 min-w-0 text-left">
                  <div
                    className="text-[13px] font-semibold truncate flex items-center gap-1.5"
                    style={{ color: theme.textPrimary }}
                  >
                    {loc.name}
                    {isCurrent && (
                      <span
                        className="text-[8.5px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full font-bold"
                        style={{
                          background: theme.accent,
                          color: dark ? "#0f172a" : "#ffffff",
                        }}
                      >
                        On screen
                      </span>
                    )}
                  </div>
                  <div
                    className="text-[10.5px] truncate flex items-center gap-1"
                    style={{ color: theme.textMuted }}
                  >
                    <MapPin size={9} weight="bold" />
                    {[loc.admin1, loc.country].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <span
                  className="shrink-0 text-[10px] font-bold uppercase tracking-[0.14em]"
                  style={{ color: loc.alerts_enabled ? "#047857" : theme.textMuted }}
                >
                  {busyId === loc.location_id ? "…" : loc.alerts_enabled ? "On" : "Off"}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={sendTest}
        disabled={testing}
        data-testid="alerts-send-test"
        className="mt-2 w-full flex items-center justify-center gap-2 rounded-2xl py-2.5 text-[12px] font-semibold disabled:opacity-60 transition-colors"
        style={{
          background: rowBg,
          border: `1px solid ${rowBorder}`,
          color: theme.textPrimary,
        }}
      >
        <PaperPlaneTilt size={13} weight="bold" />
        {testing ? "Sending…" : "Send a test alert"}
      </button>
    </div>
  );
}
